/**
 * Placement input — a single tap/press, not a hold gesture (unlike
 * round-stage.ts's hold-to-release: the spec's mechanic is an instantaneous
 * placement, not a timed hold). `pointerdown`, not `click` — fires
 * immediately with no browser click-detection delay, and unifies mouse/
 * touch/pen in one listener, the same reasoning round-stage.ts already
 * standardised on pointer events for.
 *
 * No debounce/lock beyond whatever the caller's own state guard provides:
 * `onPlace` must stay fully synchronous (no `await` in the call chain) so
 * two rapid inputs simply resolve as two real consecutive placements
 * against whatever is current at each call — correct, not a bug. If this
 * handler is ever made async, that guarantee breaks and a real debounce
 * would become necessary.
 */
export function bindStackTowerInput(target: HTMLElement, onPlace: () => void): () => void {
  const onPointerDown = (event: PointerEvent) => {
    event.preventDefault();
    onPlace();
  };
  // touchstart's own preventDefault: pointer events alone don't stop mobile
  // scroll/selection while a finger stays down — { passive: false } is
  // required for preventDefault to actually take effect on touch.
  const onTouchStart = (event: TouchEvent) => event.preventDefault();

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.code !== 'Space' && event.code !== 'Enter') return;
    if (event.repeat) return; // guard OS key-repeat from firing repeated placements while held
    event.preventDefault();
    onPlace();
  };

  target.addEventListener('pointerdown', onPointerDown);
  target.addEventListener('touchstart', onTouchStart, { passive: false });
  window.addEventListener('keydown', onKeyDown);

  return function unbind() {
    target.removeEventListener('pointerdown', onPointerDown);
    target.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('keydown', onKeyDown);
  };
}
